/**
 * Button Component
 *
 * A reusable button component with:
 * - Multiple variants (primary, secondary, danger)
 * - Multiple sizes (sm, md, lg)
 * - Loading state with spinner
 * - Disabled state styling
 *
 * This component provides consistent button styling throughout the app.
 */

import clsx from 'clsx';

/**
 * Button Component
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {string} [props.variant='primary'] - Button style variant
 * @param {string} [props.size='md'] - Button size
 * @param {boolean} [props.loading=false] - Show loading spinner
 * @param {boolean} [props.disabled=false] - Disable button
 * @param {string} [props.type='button'] - Button type attribute
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onClick] - Click handler
 *
 * @example
 * // Primary button
 * <Button onClick={handleClick}>Save</Button>
 *
 * @example
 * // Loading state
 * <Button loading={isSubmitting}>Saving...</Button>
 *
 * @example
 * // Secondary variant
 * <Button variant="secondary">Cancel</Button>
 *
 * @example
 * // Form submit button
 * <Button type="submit" size="lg">Create Account</Button>
 */
function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  type = 'button',
  className,
  onClick,
  ...props
}) {
  // Button is disabled when explicitly disabled OR when loading
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      className={clsx(
        // Base styles applied to all buttons
        'inline-flex items-center justify-center font-medium rounded-md',
        'transition-colors duration-200',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',

        // Size variants
        size === 'sm' && 'px-3 py-1.5 text-sm',
        size === 'md' && 'px-4 py-2 text-sm',
        size === 'lg' && 'px-6 py-3 text-base',

        // Color variants
        variant === 'primary' && [
          'bg-blue-600 text-white',
          'hover:bg-blue-700',
          'focus:ring-blue-500',
          'disabled:bg-blue-400'
        ],

        variant === 'secondary' && [
          'bg-white text-gray-700',
          'border border-gray-300',
          'hover:bg-gray-50',
          'focus:ring-blue-500',
          'disabled:bg-gray-100'
        ],

        variant === 'danger' && [
          'bg-red-600 text-white',
          'hover:bg-red-700',
          'focus:ring-red-500',
          'disabled:bg-red-400'
        ],

        variant === 'danger-outline' && [
          'bg-white text-red-600',
          'border border-red-300',
          'hover:bg-red-50',
          'focus:ring-red-500',
          'disabled:bg-red-50'
        ],

        variant === 'success' && [
          'bg-green-600 text-white',
          'hover:bg-green-700',
          'focus:ring-green-500',
          'disabled:bg-green-400'
        ],

        // Disabled state
        isDisabled && 'cursor-not-allowed opacity-70',

        // Additional custom classes
        className
      )}
      {...props}
    >
      {/* Loading spinner */}
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}

      {/* Button content */}
      {children}
    </button>
  );
}

export default Button;
